import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

// UI Components
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Eye, EyeOff } from 'lucide-react';
import { useChangePasswordMutation } from '@/store/api/adminSlice';
import { useChangePlatformPasswordMutation } from '@/store/api/platformApi';
import { useCurrentUser } from '@/hooks';
import toast from 'react-hot-toast';

// ✅ Zod Schema - validates trimmed passwords
const pass = z
  .string()
  .min(1, 'Password is required')
  .transform((val) => val.trim())
  .pipe(
    z
      .string()
      .min(6, 'Must be at least 6 characters')
      .max(16, 'Must be at most 16 characters')
      .refine((v) => /[A-Z]/.test(v), 'Include at least one uppercase letter')
      .refine((v) => /[a-z]/.test(v), 'Include at least one lowercase letter')
      .refine((v) => /\d/.test(v), 'Include at least one number')
      .refine((v) => /[@$!%*?&]/.test(v), 'Include at least one special character')
  );

const ResetPasswordSchema = z
  .object({
    oldPassword: pass,
    newPassword: pass,
  })
  .refine((d) => d.oldPassword !== d.newPassword, {
    message: 'New password must be different from old password',
    path: ['newPassword'],
  });

type ResetPasswordFormData = z.infer<typeof ResetPasswordSchema>;

interface ResetPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({ isOpen, onClose }) => {
  const user = useCurrentUser();
  const isAdmin = user === 'admin';

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const [changePassword, { isLoading }] = useChangePasswordMutation();
  const [changePlatformPassword, { isLoading: isPlatformLoading }] =
    useChangePlatformPasswordMutation();

  // ✅ RHF
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(ResetPasswordSchema),
    defaultValues: {
      oldPassword: '',
      newPassword: '',
    },
  });

  const onSubmit = async (data: ResetPasswordFormData) => {
    try {
      if (isAdmin) {
        await changePassword(data).unwrap();
        toast.success('Password changed successfully.');
      } else {
        await changePlatformPassword(data).unwrap();
        toast.success('Password changed successfully.');
      }
      reset();
      onClose();
    } catch (err: any) {
      console.error('Change password failed:', err);

      // Show toast error message
      const errorMessage =
        err?.data?.message || err?.message || 'Failed to change password. Please try again.';
      toast.error(errorMessage);
    }
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  const currentLoading = isAdmin ? isLoading : isPlatformLoading;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px] p-6 bg-white rounded-lg shadow-xl">
        <DialogHeader className="flex justify-between items-center flex-row pb-4 border-b border-gray-200">
          <DialogTitle className="text-xl font-semibold text-gray-800">Change Password</DialogTitle>
          <DialogClose asChild />
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="grid gap-4 py-4">
          {/* Old Password */}
          <div className="space-y-2">
            <label htmlFor="oldPassword" className="text-sm font-medium text-gray-700">
              Old Password<span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Input
                id="oldPassword"
                type={showOldPassword ? 'text' : 'password'}
                {...register('oldPassword')}
                className="mt-1 block w-full pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 p-0 text-gray-500 hover:bg-gray-100"
                onClick={() => setShowOldPassword(!showOldPassword)}
              >
                {showOldPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            {errors.oldPassword && (
              <p className="text-red-500 text-sm">{errors.oldPassword.message}</p>
            )}
          </div>

          {/* New Password */}
          <div className="space-y-2">
            <label htmlFor="newPassword" className="text-sm font-medium text-gray-700">
              New Password<span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Input
                id="newPassword"
                type={showNewPassword ? 'text' : 'password'}
                {...register('newPassword')}
                className="mt-1 block w-full pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 p-0 text-gray-500 hover:bg-gray-100"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            {errors.newPassword && (
              <p className="text-red-500 text-sm">{errors.newPassword.message}</p>
            )}
          </div>

          {/* Footer */}
          <DialogFooter className="flex flex-row justify-end gap-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="ghost"
              className="bg-red-500 hover:bg-red-600 text-white"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={currentLoading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {currentLoading ? 'Updating...' : 'Update'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ResetPasswordModal;